import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, LogIn, X, Sun, Moon, Github } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import logo from "../assets/logo.png";

const NAV_LINKS = [
  { path: "/", label: "Home" },
  { path: "/news", label: "News" },
  { path: "/events", label: "Events" },
  { path: "/team", label: "Team" },
  { path: "/gallery", label: "Gallery" },
  { path: "/tracker", label: "Tracker" },
  { path: "/apply", label: "Bewerben" },
];

import { API } from "@/config";
import { useAuth } from "@/context/AuthContext";

export const Navbar = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [appsOpen, setAppsOpen] = useState(true);
  const navContainerRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, visible: false });

  const hubTarget = user ? "/drivershub/home" : "/drivershub/login";

  const prefetchHub = () => {
    import("@/drivershub/components/ManagementLayout").catch(() => {});
    import("@/drivershub/pages/MgmtHomePage").catch(() => {});
    import("@/drivershub/pages/MgmtLoginPage").catch(() => {});
  };


  useEffect(() => {
    fetch(`${API}/settings/applications`)
      .then(res => res.json())
      .then(data => setAppsOpen(data.open))
      .catch(() => setAppsOpen(true));
  }, []);

  const visibleLinks = NAV_LINKS.filter(l => l.path !== "/apply" || appsOpen);

  useLayoutEffect(() => {
    const updateIndicator = () => {
      if (!navContainerRef.current) return;
      const activeEl = navContainerRef.current.querySelector('[data-active="true"]');
      if (activeEl) {
        const cRect = navContainerRef.current.getBoundingClientRect();
        const aRect = activeEl.getBoundingClientRect();
        setIndicator({
          left: aRect.left - cRect.left,
          width: aRect.width,
          visible: true,
        });
      } else {
        setIndicator(prev => ({ ...prev, visible: false }));
      }
    };

    updateIndicator();
    window.addEventListener("resize", updateIndicator);
    return () => window.removeEventListener("resize", updateIndicator);
  }, [location.pathname, visibleLinks.length]);

  return (
    <nav
      className="glass-nav fixed top-0 left-0 right-0 z-50 h-20"
      data-testid="navbar"
    >
      <div className="absolute inset-0 -z-10 bg-black/40" />
      <div className="max-w-7xl mx-auto h-full px-6 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo-link">
          <img src={logo} alt="Open Pipe Club" className="h-10 w-10 rounded-lg object-contain" />
          <span className="font-['Unbounded'] font-bold text-lg tracking-tight text-white hidden sm:block">
            Open Pipe Club
          </span>
        </Link>

        {/* Desktop Nav */}
        <div ref={navContainerRef} className="relative hidden lg:flex items-center gap-2">
          {visibleLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                data-active={isActive ? "true" : "false"}
                className={`relative px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
                  isActive
                    ? "text-white"
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
          {indicator.visible && (
            <motion.span
              className="absolute -bottom-[3px] h-0.5 rounded-full bg-amber-400 pointer-events-none"
              initial={false}
              animate={{
                left: indicator.left + 12,
                width: Math.max(0, indicator.width - 24),
                opacity: 1,
              }}
              transition={{ type: "spring", stiffness: 450, damping: 35 }}
            />
          )}
        </div>

        {/* Management Login */}
        <div className="hidden lg:flex items-center gap-3">
          <Link
            to={hubTarget}
            onMouseEnter={prefetchHub}
          >
            <Button
              variant="outline"
              className="text-xs font-bold uppercase tracking-wider border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-black transition-colors duration-300"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Drivers Hub
            </Button>
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <div className="lg:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="nav-mobile-menu-btn"
                className="text-slate-300 hover:text-white bg-white/5 border border-white/10 hover-glow rounded-xl"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-black/80 backdrop-blur-xl border-l-2 border-amber-400/20 w-80 shadow-[-50px_0_100px_rgba(0,0,0,0.9)]"
            >
              <SheetTitle className="text-white font-['Unbounded'] font-black uppercase tracking-tighter italic text-xs flex items-center gap-3 mb-4">
                <img src={logo} alt="Open Pipe Club" className="h-7 w-7 rounded-lg" />
                Open Pipe Club
              </SheetTitle>
              <div className="mt-8 flex flex-col gap-2">
                {visibleLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`nav-mobile-${link.label.toLowerCase()}-link`}
                    className={`px-4 py-3 rounded-md text-sm font-bold uppercase tracking-wider transition-colors duration-300 flex items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${location.pathname === link.path
                      ? "text-white bg-amber-400/10"
                      : "text-slate-400 hover:text-white hover:bg-white/5"
                      }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="border-t border-white/10 mt-4 pt-4 flex flex-col gap-3">
                  <Link to={hubTarget} onClick={() => setMobileOpen(false)} onTouchStart={prefetchHub}>
                    <Button
                      variant="outline"
                      className="w-full text-sm font-bold uppercase tracking-wider border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-black transition-colors duration-300 h-12"
                    >
                      <LogIn className="w-4 h-4 mr-2" />
                      Drivers Hub
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};